import { Add as AddIcon } from "@mui/icons-material";
import { Alert, Box, Button, Paper, Snackbar, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ItemStatusDetailModal,
  ItemStatusFiltersPanel,
  ItemStatusFormModal,
  ItemStatusStatisticsCards,
  ItemStatusTable,
} from "../../components/backend/itemstatus";
import {
  useItemStatuses,
  useItemStatusMutations,
  useItemStatusStatistics,
  useOptimisticItemStatusUpdate,
} from "../../hooks/useItemStatus";
import {
  BulkUpdateItemStatusRequest,
  CreateItemStatusRequest,
  ItemStatus,
  ItemStatusFilters,
  ItemStatusModalState,
  UpdateItemStatusRequest,
} from "../../types/admin";

import AdminPage from "./AdminPage";

const ItemStatusPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters and pagination
  const [filters, setFilters] = useState<ItemStatusFilters>({
    pageNumber: 1,
    pageSize: 20,
    sortBy: "sortOrder",
    sortDescending: false,
  });

  // State for selected statuses
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  // State for modals
  const [modalState, setModalState] = useState<ItemStatusModalState>({
    open: false,
    mode: "view",
  });

  // State for notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // Data fetching hooks
  const {
    data: statusesData,
    isLoading: isLoadingStatuses,
    error: statusesError,
    refetch: refetchStatuses,
  } = useItemStatuses(filters);

  const {
    data: statisticsData,
    isLoading: isLoadingStatistics,
    error: statisticsError,
  } = useItemStatusStatistics();

  // Mutation hooks
  const {
    createStatus,
    updateStatus,
    deleteStatus,
    toggleStatusActive,
    bulkUpdateStatusActive,
    reorderStatuses,
  } = useItemStatusMutations();

  // Optimistic update hook
  const { optimisticUpdate } = useOptimisticItemStatusUpdate();

  // Handlers
  const handleCreateStatus = async (data: CreateItemStatusRequest) => {
    try {
      await createStatus.mutateAsync(data);
      setNotification({
        open: true,
        message: t("admin.itemStatuses.createSuccess"),
        severity: "success",
      });
      setModalState({ open: false, mode: "view" });
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.createError"),
        severity: "error",
      });
    }
  };

  const handleUpdateStatus = async (
    id: number,
    data: UpdateItemStatusRequest,
  ) => {
    try {
      await updateStatus.mutateAsync({ id, data });
      setNotification({
        open: true,
        message: t("admin.itemStatuses.updateSuccess"),
        severity: "success",
      });
      setModalState({ open: false, mode: "view" });
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.updateError"),
        severity: "error",
      });
    }
  };

  const handleDeleteStatus = async (id: number) => {
    try {
      await deleteStatus.mutateAsync(id);
      setNotification({
        open: true,
        message: t("admin.itemStatuses.deleteSuccess"),
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.deleteError"),
        severity: "error",
      });
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await toggleStatusActive.mutateAsync(id);
      setNotification({
        open: true,
        message: t("admin.itemStatuses.toggleSuccess"),
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.toggleError"),
        severity: "error",
      });
    }
  };

  const handleBulkUpdateActive = async (isActive: boolean) => {
    if (selectedStatuses.length === 0) return;

    try {
      const request: BulkUpdateItemStatusRequest = {
        statusIds: selectedStatuses,
        isActive,
      };
      await bulkUpdateStatusActive.mutateAsync(request);
      setNotification({
        open: true,
        message: isActive ? t("admin.itemStatuses.bulkActivateSuccess") : t("admin.itemStatuses.bulkDeactivateSuccess"),
        severity: "success",
      });
      setSelectedStatuses([]);
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.bulkUpdateError"),
        severity: "error",
      });
    }
  };

  const handleReorder = async (reorderedStatuses: ItemStatus[]) => {
    try {
      const request = {
        statuses: reorderedStatuses.map((status, index) => ({
          id: status.id,
          sortOrder: index + 1,
        })),
      };
      await reorderStatuses.mutateAsync(request);
      setNotification({
        open: true,
        message: t("admin.itemStatuses.reorderSuccess"),
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: t("admin.itemStatuses.reorderError"),
        severity: "error",
      });
    }
  };

  const handleViewStatus = (status: ItemStatus) => {
    setModalState({
      open: true,
      mode: "view",
      status,
    });
  };

  const handleEditStatus = (status: ItemStatus) => {
    setModalState({
      open: true,
      mode: "edit",
      status,
    });
  };

  const handleCreateNew = () => {
    setModalState({
      open: true,
      mode: "create",
    });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, mode: "view" });
  };

  const handleRefresh = () => {
    refetchStatuses();
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    setNotification({
      open: true,
      message: t("admin.itemStatuses.exportComingSoon"),
      severity: "info",
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <AdminPage>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Statistics Cards */}
        <ItemStatusStatisticsCards
          data={statisticsData?.data}
          isLoading={isLoadingStatistics}
          error={statisticsError}
        />

        {/* Filters Panel */}
        <Paper sx={{ p: 2 }}>
          <ItemStatusFiltersPanel
            filters={filters}
            onFiltersChange={setFilters}
            selectedCount={selectedStatuses.length}
            onBulkActivate={() => handleBulkUpdateActive(true)}
            onBulkDeactivate={() => handleBulkUpdateActive(false)}
            onRefresh={handleRefresh}
            onExport={handleExport}
          />
        </Paper>

        {/* Actions Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {t("admin.itemStatuses.title")} ({statusesData?.data?.totalCount || 0})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{ minWidth: 140 }}
          >
            Add Status
          </Button>
        </Box>

        {/* Status Table */}
        <Paper sx={{ overflow: "hidden" }}>
          <ItemStatusTable
            data={statusesData?.data}
            isLoading={isLoadingStatuses}
            error={statusesError}
            selectedStatuses={selectedStatuses}
            onSelectionChange={setSelectedStatuses}
            onView={handleViewStatus}
            onEdit={handleEditStatus}
            onDelete={handleDeleteStatus}
            onToggleActive={handleToggleActive}
            onReorder={handleReorder}
            onRefresh={handleRefresh}
          />
        </Paper>

        {/* Modals */}
        <ItemStatusFormModal
          open={
            modalState.open &&
            (modalState.mode === "create" || modalState.mode === "edit")
          }
          mode={modalState.mode === "create" ? "create" : "edit"}
          status={modalState.status}
          onSubmit={
            modalState.mode === "create"
              ? handleCreateStatus
              : (data: UpdateItemStatusRequest) =>
                  handleUpdateStatus(modalState.status?.id || 0, data)
          }
          onClose={handleCloseModal}
        />

        <ItemStatusDetailModal
          open={modalState.open && modalState.mode === "view"}
          status={modalState.status}
          onClose={handleCloseModal}
          onEdit={handleEditStatus}
        />

        {/* Notifications */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default ItemStatusPage;
